import { useCallback, useRef, useState } from 'react'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type { UploadResponse } from './types'

export type UploadQueueStatus = 'queued' | 'uploading' | 'uploaded' | 'error'

export interface UploadQueueItem {
  /** Stable client-side id so React keys survive list re-renders. */
  clientId: string
  file: File
  status: UploadQueueStatus
  /** Set on `uploaded` — the server-side intake row id. Drives drawer
   *  → master-list deep-link after upload completes. */
  intakeId?: string
  /** True when DocumentRepo deduped the upload (same sha256 already
   *  present). UI treats deduped as a success but flags it for
   *  visibility. */
  deduped?: boolean
  /** User-facing error message on `error` status. */
  errorMessage?: string
}

/**
 * Upload queue with 3-wide concurrency. Mirrors `useCofaUploadQueue` but
 * simpler: sales-intake doesn't have a needs_vendor / needs_confirm
 * inline branch — uploads either succeed (server enqueues Celery
 * extraction) or error. Per-row status from there is observed via
 * `useIntakeList` polling.
 *
 * Why 3-wide: same answer as CofA — keeps the user from waiting on a
 * sequential queue when they drop 10 PDFs at once, but caps server
 * load on the `/api/sales-intake/upload` endpoint.
 */
const CONCURRENCY = 3

let _id = 0
function nextId() {
  _id += 1
  return `upload-${_id}-${Date.now()}`
}

export interface UseSalesIntakeUploadQueueOpts {
  /** Called once per successful upload. Host page invalidates queries. */
  onItemUploaded?: (item: UploadQueueItem) => void
}

export function useSalesIntakeUploadQueue(
  opts: UseSalesIntakeUploadQueueOpts = {},
) {
  const [items, setItems] = useState<UploadQueueItem[]>([])
  const inFlightRef = useRef(0)
  const onItemUploaded = opts.onItemUploaded

  const updateItem = useCallback(
    (clientId: string, patch: Partial<UploadQueueItem>) => {
      setItems((prev) =>
        prev.map((it) => (it.clientId === clientId ? { ...it, ...patch } : it)),
      )
    },
    [],
  )

  const pumpRef = useRef<() => void>(() => {})

  const processOne = useCallback(
    async (item: UploadQueueItem) => {
      inFlightRef.current += 1
      updateItem(item.clientId, { status: 'uploading' })
      try {
        const fd = new FormData()
        fd.append('file', item.file)
        const result = await jsonFetch<UploadResponse>(
          '/api/sales-intake/upload',
          { method: 'POST', rawBody: fd },
        )
        const next: Partial<UploadQueueItem> = {
          status: 'uploaded',
          intakeId: result.intake_id,
          deduped: result.deduped,
        }
        updateItem(item.clientId, next)
        onItemUploaded?.({ ...item, ...next })
      } catch (err) {
        updateItem(item.clientId, {
          status: 'error',
          errorMessage: err instanceof Error ? err.message : 'upload failed',
        })
      } finally {
        inFlightRef.current -= 1
        // Re-pump so the next queued item picks up the freed slot.
        pumpRef.current()
      }
    },
    [onItemUploaded, updateItem],
  )

  /** Pull `queued` items off the front of the queue up to CONCURRENCY. */
  const pump = useCallback(() => {
    setItems((current) => {
      const queued = current.filter((it) => it.status === 'queued')
      const slots = Math.max(0, CONCURRENCY - inFlightRef.current)
      const next = queued.slice(0, slots)
      // Fire the uploads outside the setState to avoid recursive
      // setState during the same render.
      queueMicrotask(() => {
        for (const item of next) {
          void processOne(item)
        }
      })
      return current
    })
  }, [processOne])

  // Keep pumpRef pointed at the current pump so processOne's finally
  // block re-pumps with fresh state.
  // eslint-disable-next-line react-hooks/refs -- intentional always-current ref pattern; processOne reads through pumpRef.current in its finally, where the latest pump (capturing fresh queue state) must be invoked.
  pumpRef.current = pump

  /** Add files to the queue + start pumping. */
  const enqueue = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files)
      if (list.length === 0) return
      setItems((prev) => [
        ...prev,
        ...list.map(
          (file): UploadQueueItem => ({
            clientId: nextId(),
            file,
            status: 'queued',
          }),
        ),
      ])
      // Pump on next tick so the new items appear before the dispatch
      queueMicrotask(pump)
    },
    [pump],
  )

  /** Remove a single item by clientId. Cancels in-flight if uploading
   *  is still possible (we don't actually abort the network call —
   *  just hide the row). */
  const removeItem = useCallback((clientId: string) => {
    setItems((prev) => prev.filter((it) => it.clientId !== clientId))
  }, [])

  /** Clear all `uploaded` and `error` rows so the queue UI stays clean
   *  after a long batch. Doesn't touch in-flight or queued items. */
  const clearTerminal = useCallback(() => {
    setItems((prev) =>
      prev.filter((it) => it.status !== 'uploaded' && it.status !== 'error'),
    )
  }, [])

  const busy = items.some(
    (it) => it.status === 'queued' || it.status === 'uploading',
  )

  return {
    items,
    enqueue,
    removeItem,
    clearTerminal,
    busy,
  }
}
